import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loginMmt } from "../mmtUserList/mmtApi";
import {
  buildSessionPath,
  getStoredSession,
  setStoredSession,
} from "../mmtUserList/mmtFlow";
import {
  ADMIN_CHOICE,
  FORGOT_PASSWORD,
  USER_LIST,
} from "../../routes/routerPath";
import { useSection } from "../../i18n/useSection";
import LanguageSwitcher from "../common/LanguageSwitcher";
import { isRestorableAppUrl } from "../../utlis/lastVisitedUrl";
import { getCurrentLocale } from "../../utlis/locale";
import AuthLayout from "./AuthLayout";
import AuthTerms from "./AuthTerms";
import "./mmtlogin.scss";

const EMAIL_REGEX = /^\s*[a-z0-9._+-]+@[a-z0-9.-]+\.[a-z]{2,}\s*$/i;

export default function MmtLogin() {
  const navigate = useNavigate();
  const t = useSection("mmtLogin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [fieldError, setFieldError] = useState(null);
  const [expiredEmail, setExpiredEmail] = useState("");
  const [loading, setLoading] = useState(false);

  // Already signed in (e.g. the app was relaunched): skip the login screen
  // and restore the last visited page. If the stored token has expired, the
  // first API call will 401 and the interceptors bring the user back here.
  useEffect(() => {
    const session = getStoredSession();
    if (!session?.accessToken) return;
    const lastUrl = localStorage.getItem("lastVisitedUrl");
    if (isRestorableAppUrl(lastUrl)) {
      navigate(lastUrl, { replace: true });
      return;
    }
    if (lastUrl) localStorage.removeItem("lastVisitedUrl");
    const target =
      session.role === "ADMIN"
        ? ADMIN_CHOICE
        : buildSessionPath(USER_LIST, session);
    navigate(target, { replace: true });
  }, []);

  const goToPasswordReset = (emailToReset = email.trim(), autoSend = false) => {
    navigate(FORGOT_PASSWORD, {
      state: {
        email: emailToReset,
        autoSend,
      },
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedEmail = email.trim();
    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setFieldError({ field: "email", message: t.error_invalid_email });
      document.getElementById("login-email")?.focus();
      return;
    }
    if (!password) {
      setFieldError({ field: "password", message: t.error_missing_password });
      document.getElementById("login-password")?.focus();
      return;
    }

    setLoading(true);
    setFieldError(null);
    setError("");
    setExpiredEmail("");
    try {
      const { session } = await loginMmt(trimmedEmail, password);
      // The login URL can still contain the boot locale after the user changes
      // the switcher. Persist the live selection, not that stale URL value.
      session.locale = getCurrentLocale();
      setStoredSession(session);
      const target =
        session.role === "ADMIN"
          ? ADMIN_CHOICE
          : buildSessionPath(USER_LIST, session);
      navigate(target, { replace: true });
    } catch (err) {
      if (err?.isPasswordExpired) {
        setExpiredEmail(trimmedEmail);
        setError(t.error_password_expired);
      } else {
        setError(err?.message || t.error_invalid_credentials);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout screen={t.title} toolbar={<LanguageSwitcher />}>
      <form
        className="mmt-login-card"
        onSubmit={handleSubmit}
        noValidate
        aria-busy={loading}
      >
        <h2 tabIndex={-1}>{t.title}</h2>
        {error ? (
          <div className="mmt-login-error" role="alert">
            <span>{error}</span>
            {expiredEmail ? (
              <div className="mmt-login-error-actions">
                <button
                  type="button"
                  onClick={() => goToPasswordReset(expiredEmail, true)}
                >
                  {t.reset_password}
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="mmt-field">
          <label htmlFor="login-email">{t.email_label}</label>
          <input
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              setError("");
              setFieldError(null);
              setExpiredEmail("");
            }}
            id="login-email"
            aria-invalid={fieldError?.field === "email"}
            aria-describedby={
              fieldError?.field === "email" ? "login-email-error" : undefined
            }
            type="email"
            autoCapitalize="off"
            spellCheck={false}
            inputMode="email"
            autoComplete="username"
            placeholder="name@organization.com"
            disabled={loading}
          />
          {fieldError?.field === "email" && (
            <p className="mmt-field-error" id="login-email-error" role="alert">
              {fieldError.message}
            </p>
          )}
        </div>

        <div className="mmt-field">
          <label htmlFor="login-password">{t.password_label}</label>
          <div className="mmt-password-wrap">
            <input
              id="login-password"
              aria-invalid={fieldError?.field === "password"}
              aria-describedby={
                fieldError?.field === "password"
                  ? "login-password-error"
                  : undefined
              }
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                setError("");
                setFieldError(null);
                setExpiredEmail("");
              }}
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              disabled={loading}
            />
            <button
              type="button"
              className="mmt-icon-button"
              onClick={() => setShowPassword((value) => !value)}
              aria-label={showPassword ? t.hide_password : t.show_password}
              aria-controls="login-password"
              aria-pressed={showPassword}
            >
              {showPassword ? t.hide : t.show}
            </button>
          </div>
          {fieldError?.field === "password" && (
            <p
              className="mmt-field-error"
              id="login-password-error"
              role="alert"
            >
              {fieldError.message}
            </p>
          )}

          <button
            type="button"
            className="mmt-text-link"
            onClick={() => goToPasswordReset(email.trim())}
          >
            {t.forgot_password}
          </button>
        </div>

        <button className="mmt-primary-button" type="submit" disabled={loading}>
          {loading ? t.submitting : t.submit}
        </button>

        <AuthTerms />
        <p className="mmt-new-user">
          <strong>{t.new_user}</strong>{" "}
          <button type="button" onClick={() => navigate("/signup")}>
            {t.sign_up}
          </button>
        </p>
      </form>
    </AuthLayout>
  );
}
